import {
  Divider,
  Grid2,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";

const Section5 = () => {
  return (
    <>
      <Typography variant="h4" gutterBottom>
        Purpose and Legal Basis for Processing
      </Typography>
      <Typography sx={{ fontSize: "1.1rem" }} variant="body1" mb={2}>
        Employee data is processed strictly for legitimate organizational
        purposes, including workforce management, attendance tracking, payroll
        processing, performance evaluation, compliance with labour and
        employment laws and internal operational analysis.
      </Typography>
      <Typography sx={{ fontSize: "1.1rem" }} variant="body1" mb={2}>
        Processing is carried out based on employment contractual necessity,
        legal obligations and the Company’s legitimate business interests.
        Employee data is never used for marketing, advertising, or
        non-employment-related purposes.
      </Typography>

      <Divider sx={{ my: 2 }} />
      <Typography variant="h4" gutterBottom>
        Data Storage and Retention
      </Typography>
      <Typography sx={{ fontSize: "1.1rem" }} variant="body1" mb={2}>
        All employee data is securely stored on cloud infrastructure hosted by
        <b>Amazon Web Services (AWS)</b> . Appropriate technical and
        organizational safeguards are implemented to protect data against
        unauthorized access, loss, alteration, or misuse.
      </Typography>
      <Typography sx={{ fontSize: "1.1rem" }} variant="body1" mb={2}>
        Data is retained in accordance with Company policy, statutory
        requirements and employment lifecycle obligations. Even after employment
        ends, certain information may be retained for legal, audit, or
        compliance purposes as required by law.
      </Typography>
      <Divider sx={{ my: 2 }} />
      <Typography variant="h4" gutterBottom>
        Data Sharing and Disclosure
      </Typography>
      <Typography sx={{ fontSize: "1.1rem" }} variant="body1" mb={2}>
        Keel Info Solution does not share employee data with third parties, does
        not use third-party analytics services and does not sell, rent, or
        monetize personal information. Employee data is disclosed only where
        required to comply with applicable legal obligations or directives from
        statutory authorities.
      </Typography>
      <Divider sx={{ my: 2 }} />
      <Typography variant="h4" gutterBottom>
        Employee Rights and Controls
      </Typography>
      <Typography sx={{ fontSize: "1.1rem" }} variant="body1" mb={2}>
        Employees have the right to:
      </Typography>
      <Grid2 container spacing={2} ml={4} mb={2}>
        <Grid2 size={{ xs: 12 }}>
          <List sx={{ listStyleType: "disc", pl: 4 }}>
            <ListItem sx={{ display: "list-item" }}>
              <ListItemText
                primary="View their personal and employment-related information"
                primaryTypographyProps={{
                  sx: { textAlign: "justify", fontSize: "1.1rem" },
                }}
              />
            </ListItem>

            <ListItem sx={{ display: "list-item" }}>
              <ListItemText
                primary="Request correction of inaccurate or outdated data"
                primaryTypographyProps={{
                  sx: { textAlign: "justify", fontSize: "1.1rem" },
                }}
              />
            </ListItem>

            <ListItem sx={{ display: "list-item" }}>
              <ListItemText
                primary="Request restriction or deletion of data, subject to Company policy and statutory requirements"
                primaryTypographyProps={{
                  sx: { textAlign: "justify", fontSize: "1.1rem" },
                }}
              />
            </ListItem>
          </List>
        </Grid2>
      </Grid2>

      <Typography sx={{ fontSize: "1.1rem" }} variant="body1" mb={2}>
        All deletion or modification requests are reviewed and approved by the
        Human Resources Department. Data download or export functionality is not
        provided to employees.
      </Typography>
      <Divider sx={{ my: 2 }} />
      <Typography variant="h4" gutterBottom>
        Account Deletion
      </Typography>
      <Typography sx={{ fontSize: "1.1rem" }} variant="body1" mb={2}>
        Employees cannot create their own accounts in the Soucient HRMS
        application. All user accounts are created and managed only by the Human
        Resources (HR) team to ensure that access is given only to authorized
        employees and that user information remains accurate and secure.
      </Typography>
      <Typography sx={{ fontSize: "1.1rem" }} variant="body1" mb={2}>
        Employees can log in using their official email ID and the password
        provided by the HR team. Users are responsible for keeping their login
        credentials confidential and should not share them with anyone.
      </Typography>
      <Typography sx={{ fontSize: "1.1rem" }} variant="body1" mb={2}>
        When an employee leaves the organization due to resignation,
        termination, or retirement, the HR team will deactivate the account
        immediately. Access to the system will be removed and the account will
        be cleared or handled according to the organization’s data management
        policies. This process helps maintain system security and prevents
        unauthorized access.
      </Typography>
      <Typography sx={{ fontSize: "1.1rem" }} variant="body1" mb={2}>
        <b>
          If an employee wishes to request account deletion, they can contact
          the Support Team at: Support@keelis.com
        </b>
      </Typography>
      <Divider sx={{ my: 2 }} />
      <Typography variant="h4" gutterBottom>
        Security Measures
      </Typography>
      <Typography sx={{ fontSize: "1.1rem" }} variant="body1" mb={2}>
        The Company maintains appropriate and industry-standard security
        practices, including role-based access control, encryption of data at
        rest and in transit, secure authentication mechanisms and regular
        security audits. Continuous monitoring is conducted to identify and
        mitigate risks. While no system can guarantee absolute security,
        reasonable safeguards are consistently maintained.
      </Typography>
      <Divider sx={{ my: 2 }} />
      <Typography variant="h4" gutterBottom>
        Data Breach Management
      </Typography>
      <Typography sx={{ fontSize: "1.1rem" }} variant="body1" mb={2}>
        In the event of a data security incident or breach, the Company will
        take appropriate containment and remediation measures, conduct an impact
        assessment and notify affected individuals and authorities as required
        under applicable laws.
      </Typography>
      <Divider sx={{ my: 2 }} />
      <Typography variant="h4" gutterBottom>
        Children’s Privacy
      </Typography>
      <Typography sx={{ fontSize: "1.1rem" }} variant="body1" mb={2}>
        Soucient HRMS is not intended for use by individuals under the age of
        18. The Application is strictly meant for employed personnel of the
        organization.
      </Typography>
      <Divider sx={{ my: 2 }} />
      <Typography variant="h4" gutterBottom>
        Legal Compliance
      </Typography>
      <Typography sx={{ fontSize: "1.1rem" }} variant="body1" mb={2}>
        This Privacy Policy is designed to comply with applicable data
        protection laws, including the Information Technology Act, 2000, the
        Digital Personal Data Protection Act of India and the General Data
        Protection Regulation (GDPR), where applicable.
      </Typography>
      <Divider sx={{ my: 2 }} />
      <Typography variant="h4" gutterBottom>
        Policy Updates
      </Typography>
      <Typography sx={{ fontSize: "1.1rem" }} variant="body1" mb={2}>
        The Company reserves the right to update or modify this Privacy Policy
        at any time. Changes will be communicated to employees via official
        email. Continued use of the Application after such updates constitutes
        acceptance of the revised Policy.
      </Typography>
      <Divider sx={{ my: 2 }} />
      <Typography variant="h4" gutterBottom>
        Governing Law and Jurisdiction
      </Typography>
      <Typography sx={{ fontSize: "1.1rem" }} variant="body1" mb={2}>
        This Privacy Policy shall be governed by and construed in accordance
        with the laws of India. Any disputes arising under this Policy shall be
        subject to the exclusive jurisdiction of the courts in Chennai, Tamil
        Nadu.
      </Typography>
    </>
  );
};

export default Section5;
